import { Dir } from '@angular/cdk/bidi';
import { F, ModifierKey, SPACE } from '@angular/cdk/keycodes';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    HostListener,
    inject,
    Injector,
    input,
    model,
    OnInit,
    output,
    runInInjectionContext,
    signal,
    viewChild,
    viewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChip } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { createNewLayout, Layout, LayoutColumn, LayoutType, SystemLayoutNames } from '@common/data-models/service/layout';
import { FieldDefValidations, ToolbarMenuCommandType, ToolbarMenuId } from '@common/enums/enums';
import { selectI18N, selectTranslatedKey } from '@common/store/i18n/selectors/i18n.selectors';
import { I18nKeys } from '@common/utils/i18n.default';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { ModalService } from '@src/suite/common/components/modal/modal.service';
import { StRoGridComponent } from '@src/suite/common/components/st-ro-grid/st-ro-grid';
import { StTextAreaOverlayComponent } from '@src/suite/common/controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { EnpsField } from '@src/suite/common/data-models/fields/enps-field';
import { GENERIC_STORY_OUTPUT_TYPE, OPTION_BLANK, TITLE_SEPARATOR } from '@src/suite/common/enums/constants';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { StRundownChooseColumnsComponent } from '@src/suite/common/st-full-screen-dialog/st-rundown-choose-columns/st-rundown-choose-columns.component';
import { MenuItem } from '@src/suite/common/st-full-screen-dialog/stories-full-screen/story-full-screen/story-actions';
import { LayoutChangeEvent } from '@src/suite/common/store/fullscreen';
import {
    selectEstimatedTimeThreshold,
    selectFieldDefsDict,
    selectRundownPageNumbersEditable,
    selectRundownTimeEstimate,
    selectTitleSeparator,
} from '@src/suite/common/store/global-tables/global-tables.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { RecordHttpActions, RundownRecordActions } from '@src/suite/common/store/records/records.actions';
import { selectRundownTemplates, selectStoryRecordByGuid, selectStoryTemplates, selectTemplateRundownRecordByGuid } from '@src/suite/common/store/records/records.selectors';
import { absDuration, isBlankLuxonDate } from '@src/suite/common/utils/date-utils';
import { generateNewSlug, takeBiggestSegmentNumber } from '@src/suite/common/utils/grid-utils';
import { ProductionCommandType } from '@src/suite/common/utils/ml-2-html-utils';
import { getRundownSecurityPrivsWrite, initializeRecord, parseLayoutColumn, rundownEqualityComparer } from '@src/suite/common/utils/record-utils';
import { getBackTimings, getFrontTimings, updateRoClocks } from '@src/suite/common/utils/timing';
import { LiveApiActions } from '@src/suite/live/store/actions/live.actions';
import { selectHasGroupLayout, selectHasUserLayout, selectStoredLayoutNamesWithoutPredefined } from '@src/suite/live/store/selectors/live.selectors';
import { DateTime, Duration } from 'luxon';
import { map } from 'rxjs';

import { ExternalActionsOverlayComponent } from '../../../playbook/components/external-actions-overlay/external-actions-overlay';
import { StRoGridComponent as StRoGridComponent_1 } from '../../components/st-ro-grid/st-ro-grid';
import { ToolbarMenuComponent } from '../../components/st-toolbar-menu/toolbar-menu/toolbar-menu.component';
import { DateTimePickerComponent } from '../../controls/date-time-picker/date-time-picker';
import { StTextAreaOverlayComponent as StTextAreaOverlayComponent_1 } from '../../controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { roActions } from './rundown-actions';

enum ModifierKeyEnum {
    ALT = 'altKey',
    SHIFT = 'shiftKey',
    CTRL = 'ctrlKey',
    META = 'metaKey',
}

enum NewRowPositionEnum {
    UP = 'up',
    DOWN = 'down',
}

@Component({
    selector: 'st-rundown-full-screen',
    imports: [
        CdkOverlayOrigin,
        DateTimePickerComponent,
        Dir,
        ExternalActionsOverlayComponent,
        MatButton,
        MatChip,
        MatDivider,
        MatIcon,
        MatIconButton,
        MatMenuTrigger,
        MatTooltip,
        StRoGridComponent_1,
        StTextAreaOverlayComponent_1,
        ToolbarMenuComponent,
    ],
    templateUrl: './st-rundown-full-screen.html',
    styleUrl: './st-rundown-full-screen.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-rundown-full-screen',
    },
})
export class StRundownFullScreenDialogComponent implements OnInit {
    PSTFieldNames = PSTFieldNames;
    NewRowPositionEnum = NewRowPositionEnum;
    fieldDefValidations = FieldDefValidations;
    latestROGuid: string;
    latestCurrentID = '';
    isFirstRun = false;
    prevCurrentIDIndex: number = null;
    pendingSelectGuids: string[] = [];
    shouldRestoreFocusInGrid = false;

    // injectables
    injector = inject(Injector);
    store: Store = inject(Store);
    globalTablesService = inject(GlobalTablesService);
    modalService = inject(ModalService);
    nomWebServicesJson = inject(NomWebServicesJson);
    dialog = inject(MatDialog);

    toolbarMenuIds: ToolbarMenuId[] = [ToolbarMenuId.TEMPLATE_CHOOSE, ToolbarMenuId.LAYOUT_SAVED];

    // view children
    readonly menuItems = viewChildren(MatMenuTrigger);
    readonly grid = viewChild<StRoGridComponent>('grid');
    readonly title = viewChild<StTextAreaOverlayComponent>('title');

    // models
    readonly data = model<StorytellingRundownRecord>();

    // inputs
    readonly layout = input<Layout>();
    readonly fullscreenArea = input<FullscreenLayoutAreas>();

    // TODO: since we have the fullscreen area input, we can remove the onLayoutSave output or even both outputs
    // outputs
    readonly storyOpen = output<string>();
    readonly layoutChange = output<LayoutChangeEvent>();

    // store signals
    readonly languageSettings = this.store.selectSignal(selectI18N)();
    readonly storyTemplates = this.store.selectSignal(selectStoryTemplates)();
    readonly pageNumbersEditable = this.store.selectSignal(selectRundownPageNumbersEditable)();
    readonly rundownTemplates = this.store.selectSignal(selectRundownTemplates);
    readonly fieldDefDict = this.store.selectSignal(selectFieldDefsDict)();

    // signals
    readonly selectedRows = signal<StorytellingStoryRecord[]>([]);
    readonly selectAtPosition = signal<{ row: number; colName: string }>(null);
    readonly localLayout = signal<Layout>(null);
    readonly isLayoutUntouched = signal(true);
    readonly columnsChanged = signal(false);
    readonly isSearchActive = signal(false);

    // computed
    readonly stories = computed(() => {
        return this.rundown()?.children || [];
    });

    readonly rundown = computed(
        () => {
            const data = {
                ...this.data(),
                TimingObj: {
                    ...this.data().TimingObj,
                },
                children: this.data().children.map(story => ({ ...story })),
            };
            const isRoTimeEstimated = this.store.selectSignal(selectRundownTimeEstimate)();
            const estimatedTimeThreshold = this.store.selectSignal(selectEstimatedTimeThreshold)();
            const currentIdIndex = data.children.findIndex(story => story.Guid === data.CurrentID);
            if (this.isFirstRun && currentIdIndex !== 0) {
                this.isFirstRun = false;
            }
            if (!data.TimingObj.IsTiming && currentIdIndex === 0 && this.prevCurrentIDIndex === -1) {
                this.isFirstRun = true;
            }

            this.prevCurrentIDIndex = currentIdIndex;
            if (data.CurrentID && !isBlankLuxonDate(data.HitTime) && (data.OnAir || data.LTTRecording)) {
                data.TimingObj.IsTiming = true;
            }
            getFrontTimings(data, false, estimatedTimeThreshold, isRoTimeEstimated, this.languageSettings, this.isFirstRun);
            getBackTimings(data, this.languageSettings);
            return { ...data, TimingObj: updateRoClocks(data, isRoTimeEstimated, estimatedTimeThreshold) };
        },
        {
            // This is intentional, but might have edge cases where it doesn't trigger.
            //If that happens dont remove this, but handle the edge cases
            // Basically this makes the grid X times more performant because it skips unnecessary updates
            equal: rundownEqualityComparer,
        }
    );

    readonly isRtl = computed(() => {
        return this.globalTablesService.getRtl();
    });

    readonly webSlugPlaceholder = computed(() => {
        return this.store.selectSignal<string>(selectTranslatedKey(I18nKeys.PSTWebSlugPlaceholder))();
    });

    readonly closeButtonLabel = computed(() => {
        return this.store.selectSignal<string>(selectTranslatedKey(I18nKeys.PSTWebCloseButtonLabel))();
    });

    readonly selectLayoutLabel = computed(() => {
        return this.store.selectSignal<string>(selectTranslatedKey(I18nKeys.PSTWebLayoutLabel))();
    });

    readonly selectProductionLabel = computed(() => {
        return this.store.selectSignal<string>(selectTranslatedKey(I18nKeys.PSTWebProductionLabel))();
    });

    readonly storiesLockStatus = computed(() => {
        const stories = this.stories().filter(story => story.Guid !== '0');
        return stories.length > 0 && stories.every(story => story.Locked);
    });

    readonly currentId = computed(() => {
        return this.data()?.CurrentID;
    });

    readonly titleValue = computed(() => {
        return this.data()?.Title ?? '';
    });

    readonly startTimeLabel = computed(() => {
        return this.fieldDefDict[PSTFieldNames.PSTRundownStartTime]?.DefaultCaption ?? '';
    });

    readonly modTimeValue = computed(() => {
        return this.data()?.ModTime ?? DateTime.now();
    });

    readonly roLayouts = computed(() => {
        return this.store.selectSignal(selectStoredLayoutNamesWithoutPredefined)();
    });

    readonly hasGroupLayout = computed(() => {
        return this.store.selectSignal(selectHasGroupLayout)();
    });

    readonly hasUserLayout = computed(() => {
        return this.store.selectSignal(selectHasUserLayout)();
    });

    readonly items = computed(() => {
        const templates = this.rundownTemplates();
        const layoutNames = this.roLayouts();
        const items: MenuItem[] = roActions;

        const checkItemAvailability = (item: MenuItem): boolean => {
            if (
                item.id === ToolbarMenuId.PRODUCTION_LOCK_RUNDOWN ||
                item.id === ToolbarMenuId.PRODUCTION_ON_AIR ||
                item.id === ToolbarMenuId.PRODUCTION_FREEZE_PAGE_NUMBERS ||
                item.id === ToolbarMenuId.PRODUCTION_MOS_CONTROL_ACTIVE ||
                item.id === ToolbarMenuId.PRODUCTION_MOS_READY_TO_AIR ||
                item.id === ToolbarMenuId.PRODUCTION_MOS_STORY_SEND ||
                item.id === ToolbarMenuId.PRODUCTION_LOCK_ALL_STORIES ||
                item.id === ToolbarMenuId.PRODUCTION_LOCK_TIMING_BAR
            ) {
                return getRundownSecurityPrivsWrite({ rec: this.data(), globalTablesService: this.globalTablesService });
            }
            if (item.type === 'menu' && !item.options?.length) {
                return false;
            }
            if ((item.id === ToolbarMenuId.INSERT_ROW_ABOVE || item.id === ToolbarMenuId.INSERT_ROW_BELOW) && !this.selectedRows()?.length) {
                return false;
            }

            if (item.id === ToolbarMenuId.PRODUCTION_SKIP_UNTIMED && !this.data()?.OnAir) {
                return false;
            }

            if (item?.commandType === ToolbarMenuCommandType.CHECKBOX && this.rundownDisabled()) {
                return false;
            }
            if (item?.id === ToolbarMenuId.PRODUCTION_RECORD && this.rundownDisabled()) {
                return false;
            }
            if ((item.id === ToolbarMenuId.RUNDOWN_PRODUCTION || item.id === ToolbarMenuId.RUNDOWN_TEMPLATE || item.id === ToolbarMenuId.INSERT) && this.rundownArchived()) {
                return false;
            }
            if (
                this.isTemplate() &&
                (item.id === ToolbarMenuId.ARCHIVE_RO || item.id === ToolbarMenuId.ARCHIVE_COPY || item.id === ToolbarMenuId.RUNDOWN_TEMPLATE || item.id !== ToolbarMenuId.STORY_SAVE_AS_TEMPLATE)
            ) {
                return false;
            }
            switch (item.id) {
                case ToolbarMenuId.INSERT_ROW_ABOVE:
                case ToolbarMenuId.INSERT_ROW_BELOW:
                    return !this.rundownDisabled() && this.selectedRows()?.length > 0;
                case ToolbarMenuId.LAYOUT_GROUP:
                    return this.hasGroupLayout();
                case ToolbarMenuId.LAYOUT_USER:
                    return this.hasUserLayout();
                case ToolbarMenuId.LAYOUT_TEMPLATE:
                    return this.data()?.TemplateLayout?.columns?.length > 0;
                case ToolbarMenuId.LAYOUT_DELETE:
                    return this.roLayout()?.type === LayoutType.Stored;
                case ToolbarMenuId.TEMPLATE_SAVE_AS:
                    return !this.isHomePath() && !this.rundownDisabled();
                case ToolbarMenuId.TEMPLATE_CHOOSE:
                    return !this.rundownDisabled();
                case ToolbarMenuId.LAYOUT_MAKE_GROUP:
                case ToolbarMenuId.LAYOUT_SAVE_NEW:
                    return !this.rundownDisabled();
                case ToolbarMenuId.RUNDOWN_EXPORT:
                case ToolbarMenuId.RUNDOWN_RENAME:
                case ToolbarMenuId.ARCHIVE_RO:
                case ToolbarMenuId.ARCHIVE_COPY:
                case ToolbarMenuId.DELETE:
                    return !this.rundownArchived();
            }
            return this.rundownEditable();
        };

        const iterateOverItems = (localItems: MenuItem[]) => {
            for (const item of localItems) {
                // NOTE: I would change that whole logic here
                // TODO: for now the only item with dynamic options is ToolbarMenuId.LAYOUT_SAVED
                if (item.options && item.options.length > 0 && !this.toolbarMenuIds.includes(item.id as ToolbarMenuId)) {
                    item.label = item.labelKey ? this.languageSettings[item.labelKey] : item.label || '';
                    iterateOverItems(item.options);
                } else {
                    switch (item.id) {
                        case ToolbarMenuId.LAYOUT_SAVED: {
                            item.options = layoutNames?.map(layoutName => {
                                return {
                                    id: ToolbarMenuId.LAYOUT_SELECTED,
                                    label: layoutName,
                                    type: 'action',
                                    selected: this.isLayoutUntouched() && this.layout()?.name === layoutName,
                                    metadata: {
                                        name: layoutName,
                                    },
                                };
                            });
                            break;
                        }
                    }

                    if (item.commandType === ToolbarMenuCommandType.CHECKBOX) {
                        switch (item.id) {
                            case ToolbarMenuId.PRODUCTION_FREEZE_PAGE_NUMBERS:
                                item.checkboxValue = this.data()?.FreezePages;
                                break;
                            case ToolbarMenuId.PRODUCTION_ON_AIR:
                                item.checkboxValue = this.data()?.OnAir;
                                break;
                            case ToolbarMenuId.PRODUCTION_RECORD:
                                item.checkboxValue = this.data()?.LTTRecording;
                                break;
                            case ToolbarMenuId.PRODUCTION_LOCK_RUNDOWN:
                                item.checkboxValue = this.data()?.Locked;
                                break;
                            case ToolbarMenuId.PRODUCTION_LOCK_ALL_STORIES:
                                item.checkboxValue = this.storiesLockStatus();
                                break;
                            case ToolbarMenuId.PRODUCTION_LOCK_TIMING_BAR:
                                item.checkboxValue = this.data()?.TimingLocked;
                                break;
                            case ToolbarMenuId.PRODUCTION_MOS_CONTROL_ACTIVE:
                                item.checkboxValue = this.data()?.MOSControl;
                                break;
                            case ToolbarMenuId.PRODUCTION_MOS_READY_TO_AIR:
                                item.checkboxValue = this.data()?.MOSAir;
                                break;
                            case ToolbarMenuId.PRODUCTION_SKIP_UNTIMED:
                                item.checkboxValue = this.data()?.SkipUntimed;
                                break;
                        }
                    } else {
                        switch (item.id) {
                            case ToolbarMenuId.LAYOUT_DEFAULT:
                                item.selected = this.isLayoutUntouched() && this.layout()?.isDefault;
                                break;
                            case ToolbarMenuId.LAYOUT_GROUP:
                                item.selected = this.isLayoutUntouched() && this.layout()?.type === LayoutType.Group;
                                break;
                            case ToolbarMenuId.LAYOUT_USER:
                                item.selected = this.isLayoutUntouched() && this.layout()?.type === LayoutType.User;
                                break;
                            case ToolbarMenuId.LAYOUT_SYSTEM:
                                item.selected = this.isLayoutUntouched() && this.layout()?.type === LayoutType.System;
                                break;
                            case ToolbarMenuId.LAYOUT_TEMPLATE:
                                item.selected = this.isLayoutUntouched() && this.layout()?.type === LayoutType.Template;
                                break;
                            case ToolbarMenuId.TEMPLATE_CHOOSE:
                                item.options = this.extractRundownTemplatesForMenu(templates);
                                break;
                        }
                    }
                    // This is specific to handle the case when saving template from manage templates page
                    if (item.id === ToolbarMenuId.TEMPLATE_SAVE_AS && this.isTemplate()) {
                        item.label = this.languageSettings[I18nKeys.PSTRundownMenuTemplateSave] || item.label || '';
                    } else {
                        item.label = item.labelKey ? this.languageSettings[item.labelKey] : item.label || '';
                    }
                }
                item.disabled = !checkItemAvailability(item);
            }
        };
        iterateOverItems(items);
        return items;
    });
    readonly roLayout = computed(
        () => {
            return this.localLayout();
        },
        {
            equal: (a, b) => {
                if (!a && !b) return true;
                if (!a && b) return false;
                if (a && !b) return false;
                if (a?.columns?.length !== b?.columns?.length) return false;
                for (let i = 0; i < a?.columns?.length; i++) {
                    if (a?.columns[i]?.name !== b?.columns[i]?.name) return false;
                    if (a?.columns[i]?.width !== b?.columns[i]?.width) return false;
                }
                return true;
            },
        }
    );

    readonly isReadOnly = computed(() => {
        const shareRundownControlUsersArray = this.data()?.ShareRundownControlUsers?.split('\u0010');
        if (
            !this.data()?.Locked ||
            !this.data()?.LockedBy ||
            this.data()?.LockedBy === this.nomWebServicesJson.UserId ||
            shareRundownControlUsersArray?.find(user => user === this.nomWebServicesJson.UserId)
        )
            return false;

        return true;
    });

    readonly titleSeparator = computed(() => {
        return this.store.selectSignal(selectTitleSeparator)() || TITLE_SEPARATOR;
    });

    extractRundownTemplatesForMenu(templates: StorytellingRundownRecord[]) {
        return templates.map(
            template =>
                ({
                    type: 'action',
                    label: template?.Title,
                    id: ToolbarMenuId.SELECT_TEMPLATE,
                    showIcon: false,
                    metadata: { Guid: template?.Guid, Type: template?.Type },
                    addDeleteBtnToAction: true,
                }) as MenuItem
        );
    }

    selectRundownTemplate(rundownGuid: string) {
        const template = this.store.selectSignal(selectTemplateRundownRecordByGuid(rundownGuid))();
        this.store.dispatch(
            LiveApiActions.openRundownPropertiesDialog({
                record: {
                    ...(template || initializeRecord(ObjectTypes.RUNDOWN)),
                    isNewRecord: true,
                    SourceGUID: template?.Guid,
                    Title: template && template?.Guid !== OPTION_BLANK ? template?.Title : '',
                },
                popUpTitle: this.languageSettings.PSTRundownCreateNewRundown,
            })
        );
    }

    readonly isHomePath = computed(() => {
        return this.data().Path.isHomePath;
    });

    readonly isTemplate = computed(() => {
        return this.data()?.Path?.isTemplate();
    });

    readonly rundownEditable = computed(() => {
        return getRundownSecurityPrivsWrite({ rec: this.data(), globalTablesService: this.globalTablesService }) && !this.data()?.Path?.isWasteBinPath;
    });

    readonly rundownDisabled = computed(() => {
        return this.isReadOnly() || !this.rundownEditable();
    });

    readonly isRundownOptionDisabled = computed(() => {
        return !getRundownSecurityPrivsWrite({ rec: this.data(), globalTablesService: this.globalTablesService }) || this.data()?.Path?.isWasteBinPath;
    });

    readonly insertRowDisabled = computed(() => {
        return this.rundownDisabled() || this.rundownArchived() || this.selectedRows()?.length === 0;
    });

    readonly rundownArchived = computed(() => {
        return this.data()?.Path?.isArchivePath ?? false;
    });

    readonly mosControlIcon = computed(() => {
        return this.data()?.MOSControl ? 'mat-icon-mos-control-active' : 'mat-icon-mos-control-not-active';
    });

    readonly lockTimingBarIcon = computed(() => {
        return this.data()?.TimingLocked ? 'mat-icon-lock-timing-bar-active' : 'mat-icon-lock-timing-bar-not-active';
    });

    readonly lockAllStoriesIcon = computed(() => {
        return this.storiesLockStatus() ? 'mat-icon-lock-all-stories-active' : 'mat-icon-lock-all-stories-not-active';
    });

    readonly mosReadyToAirIcon = computed(() => {
        return this.data()?.MOSAir ? 'mat-icon-mos-ready-to-air-active' : 'mat-icon-mos-ready-to-air-not-active';
    });

    readonly onAirIcon = computed(() => {
        return this.data()?.OnAir ? 'mat-icon-on-air-active' : 'mat-icon-on-air-not-active';
    });

    readonly lockRundownIcon = computed(() => {
        return this.data()?.Locked ? 'mat-icon-lock-rundown-active' : 'mat-icon-lock-rundown-not-active';
    });

    @HostListener('document:keyup', ['$event'])
    public handleKeyUp(event: KeyboardEvent) {
        if (event.ctrlKey && event.keyCode === F) {
            const newSearchState = !this.isSearchActive();
            this.isSearchActive.set(newSearchState);
            if (!newSearchState) {
                this.grid().clearPreviousMarkSearch();
                this.grid().clearPreviousSearch(null);
            }
            event.stopPropagation();
            event.preventDefault();
            return false;
        }
    }

    @HostListener('document:keydown', ['$event'])
    public handleKeyDown(event: KeyboardEvent) {
        if (event.ctrlKey && event.keyCode === F) {
            event.stopPropagation();
            event.preventDefault();
            return false;
        }
    }

    // methods
    @HostListener('keyup', ['$event'])
    onKeyupSpace(event: KeyboardEvent) {
        if (event.keyCode !== SPACE) return;
        if (this.data()?.PreventManualBarMovement) return;
        if (this.data()?.OnAir) {
            const isShift = event.shiftKey;
            const isCtrl = event.ctrlKey;
            const hasBothModifiers = isShift && isCtrl;
            const hasNeitherModifiers = !(isShift || isCtrl);
            const userLockedBy = this.data().TimingLockedBy;
            const canMoveBar = !this.data().TimingLocked || this.nomWebServicesJson?.HelloResults?.Supervisor || userLockedBy === this.globalTablesService.nomWebServicesJson.HelloResults.UserID;
            let currentId = this.currentId();
            const currentStoryIdx = this.stories().findIndex(story => story.Guid === currentId);
            const currentStory = this.stories()[currentStoryIdx];
            const elapsed = this.data().TimingObj.SegmentCountUp?.as('seconds') ?? 0;

            const updateStory = (story: StorytellingStoryRecord, elapsedSecs) => {
                const updateStoryFields: EnpsField[] = [
                    { key: PSTFieldNames.ElapsedTime, value: elapsedSecs },
                    { key: PSTFieldNames.Type, value: ObjectTypes.SCRIPT },
                ];
                this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: story, fieldsToUpdate: updateStoryFields }));
            };
            if (canMoveBar) {
                const fieldsToUpdate: EnpsField[] = [];
                if (hasNeitherModifiers) {
                    if (!currentId) {
                        currentId = this.getTimingBarCurrentId(this.stories()[0]?.Guid, this.stories());
                        const fixedDuration = this.data().FixedDuration;
                        if (currentId !== '0') {
                            if (fixedDuration) {
                                const roDuration = this.data().RundownDuration;
                                const newStartTime = DateTime.now();
                                fieldsToUpdate.push({ key: PSTFieldNames.PSTRundownStartTime, value: newStartTime });
                                fieldsToUpdate.push({ key: PSTFieldNames.PSTRundownEndTime, value: newStartTime.plus(roDuration) });
                            }
                        }
                    } else {
                        if (currentId !== '0' && currentId !== this.stories()[this.stories()?.length - 1]?.Guid) {
                            updateStory(currentStory, elapsed);
                            currentId = this.getTimingBarCurrentId(this.stories()[currentStoryIdx + 1]?.Guid, this.stories());
                        } else {
                            currentId = this.getTimingBarCurrentId(this.stories()[0]?.Guid, this.stories());
                        }

                        if (currentId === '0' || this.isBelowBlackLine(currentId)) {
                            currentId = '';
                        }
                    }
                } else if (hasBothModifiers) {
                    if (!currentId) {
                        return;
                    }

                    updateStory(currentStory, elapsed);
                    currentId = '';
                } else if (isShift) {
                    if (!currentId) {
                        return;
                    }
                    updateStory(currentStory, elapsed);

                    if (currentStoryIdx !== 0) {
                        currentId = this.getTimingBarCurrentId(this.stories()[currentStoryIdx - 1]?.Guid, this.stories(), ModifierKeyEnum.SHIFT);
                    } else {
                        currentId = '';
                    }
                } else if (isCtrl) {
                    const selectedRow = this.selectedRows()[0];
                    if (!this.isBelowBlackLine(this.selectedRows()?.[0]?.Guid)) {
                        if (!selectedRow || selectedRow.Guid === '0') {
                            return;
                        }
                    }
                    if (!this.shouldStoryHaveTimingBar(selectedRow)) return;

                    updateStory(currentStory, elapsed);
                    currentId = selectedRow.Guid;
                    if (currentId === '0') {
                        currentId = '';
                    }
                }

                if (!currentId || this.isBelowBlackLine(currentId)) {
                    currentId = '';
                    fieldsToUpdate.push({ key: PSTFieldNames.HitTime, value: '' });
                } else {
                    const hitTime = DateTime.now();
                    fieldsToUpdate.push({ key: PSTFieldNames.HitTime, value: hitTime });
                }

                fieldsToUpdate.push({ key: PSTFieldNames.CurrentID, value: currentId });
                this.store.dispatch(LiveApiActions.updateCurrentRundown({ data: fieldsToUpdate, guid: this.data().Guid }));
            }
        }
    }

    private _adjustScrollBasedOnTimingBar(currentId: string) {
        const grid = this.grid().flexGrid();
        const currentIdIdx = grid?.rows?.findIndex(row => row.dataItem?.Guid === currentId);
        if (grid.rows.length > 0 && currentIdIdx === -1) {
            grid.scrollIntoView(0, -1);
            return;
        }

        const currentViewRange = grid.viewRange;
        const visibleRows = currentViewRange.bottomRow - currentViewRange.topRow;
        const maxBottomRow = grid.rows.length - 1;

        // This code only scrolls the grid if the timing bar is outside the current view range.
        // This is necessary because when the timing bar is moved rapidly, the grid rows can become misaligned.
        if (currentViewRange.isValid && currentIdIdx > 0 && (currentIdIdx < currentViewRange.topRow || currentIdIdx >= currentViewRange.bottomRow)) {
            let targetTopRow: number;

            // Detect movement direction and adjust positioning
            if (currentIdIdx < currentViewRange.topRow) {
                // Moving upward - show more context above timing bar
                // Position timing bar about 2/3 down in the viewport
                const contextRows = (visibleRows * 2) / 3;
                targetTopRow = Math.max(0, Math.floor(currentIdIdx - contextRows));

                // For upward movement, scroll to the target top row
                grid.scrollIntoView(targetTopRow, -1);
            } else {
                // Moving downward - position timing bar in upper part of viewport
                targetTopRow = currentIdIdx > 0 ? currentIdIdx - 1 : 0;

                // For downward movement, calculate and scroll to bottom row
                const targetBottomRow = Math.min(maxBottomRow, targetTopRow + visibleRows);
                grid.scrollIntoView(targetBottomRow, -1);
            }
        }
    }

    ngOnInit() {
        this.store.dispatch(PlaybookApiActions.loadStoryTemplates());

        runInInjectionContext(this.injector, () => {
            effect(() => {
                if (!this.columnsChanged() || this.rundown()?.Guid !== this.latestROGuid) {
                    this.latestROGuid = this.rundown()?.Guid;
                    this.localLayout.set(Object.assign(createNewLayout(), this.layout()));
                    this.isLayoutUntouched.set(true);
                    this.columnsChanged.set(false);
                }
            });
            effect(() => {
                if (this.rundown()?.CurrentID !== this.latestCurrentID) {
                    this.latestCurrentID = this.rundown()?.CurrentID;
                    if (!this.rundown().fromRTU) {
                        setTimeout(() => {
                            this._adjustScrollBasedOnTimingBar(this.latestCurrentID);
                        }, 100);
                    }
                }
            });
            effect(() => {
                const stories = this.stories();
                const grid = this.grid()?.flexGrid();
                if (stories && this.pendingSelectGuids.length > 0) {
                    const allRows = grid.rows;
                    const newSelectedRows = [];
                    for (const guid of this.pendingSelectGuids) {
                        const idx = allRows.findIndex(row => row?.dataItem?.Guid === guid);
                        if (idx > -1 && idx + 1 < allRows.length) {
                            newSelectedRows.push(allRows[idx + 1]);
                        }
                    }
                    grid.selectedRows = newSelectedRows;
                    this.pendingSelectGuids = [];
                }
            });
        });
    }

    resizedColumn(event: { column: LayoutColumn }) {
        this.localLayout.update(layout => ({
            ...layout,
            columns: layout.columns.map(col => (col.name === event.column.name ? { ...event.column, columnList: col.columnList ?? [] } : col)),
        }));
        this.isLayoutUntouched.set(false);
        this.columnsChanged.set(true);
    }

    onMenuClosed(menuId: ToolbarMenuId | ProductionCommandType) {
        // Only restore focus to grid after the toolbar menu is closed and if is selected INSERT_ROW_ABOVE or INSERT_ROW_BELOW
        if (menuId === ToolbarMenuId.INSERT && this.shouldRestoreFocusInGrid) {
            this.shouldRestoreFocusInGrid = false;
            this.grid().flexGrid()?.focus();
        }
    }

    searchMenus() {
        this.isSearchActive.set(!this.isSearchActive());
        if (!this.isSearchActive()) {
            this.grid().clearPreviousMarkSearch();
            this.grid().clearPreviousSearch(null);
        }
    }

    showRundownProperties() {
        this.store.dispatch(
            LiveApiActions.openRundownPropertiesDialog({
                record: this.rundown(),
                popUpTitle: this.languageSettings.PSTRundownMenuProperties,
            })
        );
    }

    renameRundown() {
        this.title().onContainerClick();
    }

    exportGridToTxt(): void {
        const grid = this.grid().flexGrid();
        if (!grid) return;

        const headers = grid.columns.map(col => String(col?.header ?? '')).join('\t');
        const lines = [headers];

        for (const row of grid.rows) {
            if (!row?.dataItem) continue;

            const values = grid.columns.map(col => String(row?.dataItem?.[col?.binding] ?? ''));
            lines.push(values.join('\t'));
        }

        const content = lines.join('\n');
        const filename = `${this.data().Title || 'rundown-export'}.txt`;

        try {
            const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err: any) {
            if (err.name !== 'AbortError') {
                console.error('TXT export failed:', err);
            }
        }
    }

    addRow(position: NewRowPositionEnum) {
        if (!this.rundownEditable()) return;
        const rowData = {
            title: '',
            position,
            fieldsToUpdate: {},
        };

        rowData.fieldsToUpdate = {
            [PSTFieldNames.Title]: generateNewSlug(
                'New Row',
                this.stories().map(story => story.Title),
                '-',
                this.globalTablesService.Staff[this.globalTablesService.nomWebServicesJson.HelloResults.UserID]?.DisplayName
            ),
            [PSTFieldNames.Type]: ObjectTypes.SCRIPT,
            [PSTFieldNames.PSTStoryOutputType]: GENERIC_STORY_OUTPUT_TYPE,
        };

        if (rowData.position === NewRowPositionEnum.UP) {
            this.addRowAbove(rowData);
        } else if (rowData.position === NewRowPositionEnum.DOWN) {
            this.addRowBelow(rowData);
        }
    }

    addRowAbove(rowData: any) {
        const grid = this.grid().flexGrid();
        if (this.pendingSelectGuids.length === 0) {
            this.pendingSelectGuids = grid.selectedRows.map(row => row?.dataItem?.Guid || '');
        }
        this.store.dispatch(
            LiveApiActions.insertRundownRow({
                database: this.data().Database,
                collectionPath: this.data().Path.fullPath,
                collectionGuid: this.data().Guid,
                insertBeforeGuid: grid.selectedRows[0]?.dataItem?.Guid || '',
                fieldsToUpdate: rowData.fieldsToUpdate,
            })
        );
        grid.focus();
    }

    addRowBelow(rowData: any) {
        const grid = this.grid().flexGrid();
        const currentLastSelectedRow = grid.selectedRows[grid.selectedRows.length - 1]?.dataItem;
        const currentRowIdx = this.stories().findIndex(story => story.Guid === currentLastSelectedRow?.Guid);
        let nextRow = '';
        if (currentRowIdx > -1) {
            nextRow = this.stories()[currentRowIdx + 1]?.Guid || '';
        }

        this.store.dispatch(
            LiveApiActions.insertRundownRow({
                database: this.data().Database,
                collectionPath: this.data().Path.fullPath,
                collectionGuid: this.data().Guid,
                insertBeforeGuid: nextRow,
                fieldsToUpdate: rowData.fieldsToUpdate,
            })
        );
        grid.focus();
    }

    addRowOnCommand() {
        if (!this.rundownEditable()) return;
        const currentLastSelectedRow = this.selectedRows()[this.selectedRows().length - 1];
        const currentRowIdx = currentLastSelectedRow ? this.stories().findIndex(story => story.Guid === currentLastSelectedRow.Guid) : -1;
        const nextRow = currentRowIdx >= 0 ? this.stories()[currentRowIdx + 1]?.Guid || '' : '';

        const storiesWithSameSlug = currentLastSelectedRow ? this.stories().filter(val => val.Slug === currentLastSelectedRow.Slug) : [];
        const biggestSegmentNumber = takeBiggestSegmentNumber(storiesWithSameSlug);
        const fieldsToUpdate = {
            [PSTFieldNames.Title]: currentLastSelectedRow
                ? this.data()?.DupeSlugs
                    ? currentLastSelectedRow.Slug
                    : currentLastSelectedRow.Slug +
                      this.titleSeparator() +
                      `New Segment ${biggestSegmentNumber + 1} ${this.globalTablesService.Staff[this.globalTablesService.nomWebServicesJson.HelloResults.UserID]?.DisplayName}`
                : `New Segment ${(biggestSegmentNumber ?? 0) + 1} ${this.globalTablesService.Staff[this.globalTablesService.nomWebServicesJson.HelloResults.UserID]?.DisplayName}`,

            [PSTFieldNames.Type]: ObjectTypes.SCRIPT,
            [PSTFieldNames.PSTStoryOutputType]: GENERIC_STORY_OUTPUT_TYPE,
        };
        this.store.dispatch(
            LiveApiActions.insertRundownRow({
                database: this.data().Database,
                collectionPath: this.data().Path.fullPath,
                collectionGuid: this.data().Guid,
                insertBeforeGuid: nextRow,
                fieldsToUpdate,
            })
        );

        this.selectAtPosition.set({ row: currentRowIdx + 1, colName: 'Segment' });
    }

    insertBreak() {
        const fieldsToUpdate: Record<string, any> = {
            [PSTFieldNames.Title]: generateNewSlug(
                'New Row',
                this.stories().map(story => story.Title),
                '-',
                this.globalTablesService.Staff[this.globalTablesService.nomWebServicesJson.HelloResults.UserID]?.DisplayName
            ),
            [PSTFieldNames.Type]: ObjectTypes.SCRIPT,
            [PSTFieldNames.Break]: true,
        };
        const currentLastSelectedRow = this.selectedRows()[this.selectedRows().length - 1];
        const currentRowIdx = this.stories().findIndex(story => story.Guid === currentLastSelectedRow.Guid);
        const nextRow = this.stories()[currentRowIdx + 1]?.Guid || '0';
        this.store.dispatch(
            LiveApiActions.insertRundownRow({
                database: this.data().Database,
                collectionPath: this.data().Path.fullPath,
                collectionGuid: this.data().Guid,
                insertBeforeGuid: nextRow,
                fieldsToUpdate,
            })
        );
    }

    timezoneChanged(fieldId: string, event: any) {
        this.data.update(val => {
            val[fieldId] = event;
            return val;
        });
        this.store.dispatch(RundownRecordActions.updateRundowns({ records: [this.data()] }));
    }

    liveRecording() {
        // TODO implement live recording action
    }

    freezePageNumbers() {
        this.store.dispatch(LiveApiActions.freezeRundownPages({ guid: this.data().Guid }));
    }
    onAir() {
        this.store.dispatch(LiveApiActions.onAir({ guid: this.data().Guid }));
    }
    skipUntimed() {
        this.store.dispatch(LiveApiActions.skipUntimed({ guid: this.data().Guid }));
    }
    mosControlActive() {
        this.store.dispatch(LiveApiActions.mosControlActive({ guid: this.data().Guid }));
    }
    mosReadyToAir() {
        this.store.dispatch(LiveApiActions.mosReadyToAir({ guid: this.data().Guid }));
    }
    mosStorySend() {
        this.store.dispatch(LiveApiActions.openMOSStorySendDialog({ guid: this.data().Guid }));
    }
    lockRundown() {
        this.store.dispatch(LiveApiActions.lockRundown({ guid: this.data().Guid }));
    }
    lockAllStories() {
        this.store.dispatch(LiveApiActions.lockUnLockRundownStories({ rundown: this.data(), storiesLockStatus: !this.storiesLockStatus() }));
    }
    toggleLockingTimingBar() {
        this.store.dispatch(LiveApiActions.toggleLockingRundownTimingBar({ guid: this.data().Guid }));
    }

    makeThisMyUserLayout() {
        this.setLayoutAs(LayoutType.User);
    }

    makeThisMyGroupLayout() {
        this.setLayoutAs(LayoutType.Group);
    }

    saveAsNewLayout() {
        this.setLayoutAs(LayoutType.Stored);
    }

    deleteCurrentLayout() {
        if (this.roLayout() && this.roLayout()?.type === LayoutType.Stored && this.roLayout()?.name) {
            this.store.dispatch(LiveApiActions.deleteRundownLayout({ layoutName: this.roLayout()?.name }));
        }
    }

    openNewTemplateDialog() {
        this.store.dispatch(RecordHttpActions.openNewTemplateDialog({ record: this.data(), layout: this.localLayout(), isFullscreen: true }));
    }

    openChooseColumnsDialog() {
        this.dialog
            .open(StRundownChooseColumnsComponent, {
                hasBackdrop: true,
                disableClose: false,
                autoFocus: true,
                width: '38.625rem',
                height: '60vh',
                data: {
                    layout: this.roLayout(),
                    pageNumEditable: this.pageNumbersEditable,
                },
            })
            .afterClosed()
            .pipe(
                map(result => {
                    if (result?.columns?.length) {
                        const newLayout = Object.assign(createNewLayout(), this.localLayout());
                        newLayout.columns = result.columns;
                        this.localLayout.set(newLayout);
                        this.isLayoutUntouched.set(false);
                        this.columnsChanged.set(true);
                    }
                })
            )
            .subscribe();
    }

    setLayoutAs(layoutType: LayoutType) {
        this.store.dispatch(
            LiveApiActions.saveRundownLayout({
                params: {
                    CollectionDatabase: this.data().Database,
                    CollectionGuid: this.data().Guid,
                    CollectionType: ObjectTypes.RUNDOWN,
                    LayoutName: '',
                    LayoutType: layoutType,
                    LayoutColumns: this.localLayout().columns.map(col => parseLayoutColumn(col)),
                    fullscreenArea: this.fullscreenArea(),
                },
            })
        );
    }

    collectionChanged(event: { propID: string; value: Record<string, any>; rowIndex: number; guid: string }) {
        this.store.dispatch(LiveApiActions.updateRundownStory({ guid: event.guid, propID: event.propID, value: event.value, rowIndex: event.rowIndex, roGuid: this.data().Guid }));
    }

    openStory(event: { guid: string }) {
        this.storyOpen.emit(event.guid);
    }

    async deleteStory(event: { guid: string }) {
        if (this.isReadOnly()) {
            return;
        }

        const story = this.store.selectSignal(selectStoryRecordByGuid(event.guid))();
        if (story) {
            this.store.dispatch(RecordHttpActions.deleteRecord({ record: story }));
        }
    }

    collectionReordered(event: { sequence: string[] }) {
        const data: EnpsField[] = [{ key: PSTFieldNames.PSTRundownSequence, value: event.sequence }];
        this.store.dispatch(LiveApiActions.updateCurrentRundown({ data, guid: this.data().Guid }));
    }

    moveScriptsToCollection(event: { stories: Partial<StorytellingStoryRecord>[]; insertBeforeGuid: string; sourceRO: Partial<StorytellingRundownRecord>; copyTo: boolean }) {
        const { stories, insertBeforeGuid, sourceRO, copyTo } = event;
        const action = copyTo ? LiveApiActions.copyScriptFromRundownToRundown : LiveApiActions.moveScriptFromRundownToRundown;
        const filteredStories = stories.filter(story => story.Guid !== '0');

        this.store.dispatch(
            action({
                stories: filteredStories,
                insertBeforeGuid,
                destGuid: this.data().Guid,
                sourceRO,
            })
        );
    }

    addStoryToRundown(event: { scriptDatabase: string; scriptPath: string; scriptGuid: string; insertBeforeGuid: string; propertiesToOverride: Record<string, any>; forceReloadStory?: boolean }) {
        this.store.dispatch(LiveApiActions.addRundownStory({ ...event, guid: this.data().Guid }));
    }

    updateRundown(value: any, fieldId: string) {
        const fieldDef = [{ value, key: fieldId }];
        // TODO: not cool to have this title logic here
        if (fieldId === PSTFieldNames.Title) {
            if (!value?.trim()) {
                this.title().internalValue.set(this.data().Title);
                return;
            }
            this.data().Title = value.trim();
        }
        this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: this.data(), fieldsToUpdate: fieldDef }));
    }

    selectedRowsChanged(selectedRows: StorytellingStoryRecord[]) {
        this.selectedRows.set(selectedRows);
    }

    private getTimingBarCurrentId(guid: string, stories: StorytellingStoryRecord[], modifierKey?: ModifierKey): string {
        const storyIdx = stories.findIndex(item => item.Guid === guid);
        const story = stories[storyIdx];
        if (!story) return null;
        if (this.shouldStoryHaveTimingBar(story)) {
            return story.Guid;
        }

        if (modifierKey === ModifierKeyEnum.SHIFT) {
            for (let i = storyIdx - 1; i >= 0; i--) {
                const prevStory = stories[i];
                if (this.shouldStoryHaveTimingBar(prevStory)) {
                    return prevStory.Guid;
                }
            }
        } else {
            for (let i = storyIdx + 1; i < stories.length; i++) {
                const nextStory = stories[i];
                if (this.shouldStoryHaveTimingBar(nextStory)) {
                    return nextStory.Guid;
                }
            }
        }
        return '';
    }

    private shouldStoryHaveTimingBar(story: StorytellingStoryRecord): boolean {
        const floated = story.Float;
        const skipUntimed = this.data().SkipUntimed;
        if (floated) return false;
        if (!skipUntimed) return true;
        const useEstimated = this.store.selectSignal(selectRundownTimeEstimate)();
        const zeroTimespan = Duration.fromMillis(0);
        let timeSpan: Duration = null;
        if (useEstimated) {
            timeSpan = story.Estimated ?? Duration.fromMillis(0);
        } else {
            timeSpan = story.ReadTime ?? Duration.fromMillis(0);
        }

        if (absDuration(timeSpan) > zeroTimespan) return true;
        return false;
    }

    private isBelowBlackLine(guid: string): boolean {
        const sequence = this.data().Sequence;
        if (guid && sequence) {
            const idx = sequence.indexOf(guid);
            const blackLineIdx = sequence.indexOf('0');
            return !(idx < blackLineIdx && guid?.length > 0);
        }
        return false;
    }

    closeAllMenus() {
        if (this.menuItems()?.length > 0) {
            for (const item of this.menuItems()) {
                if (item.openMenu) {
                    item.closeMenu();
                }
            }
        }
    }

    archiveRundown(isCopy: boolean, isAboveBlackline: boolean) {
        this.store.dispatch(RundownRecordActions.archiveRundown({ record: this.data(), isCopy, isAboveBlackline }));
    }

    onActionHandler(item: MenuItem) {
        switch (item?.id) {
            // RUNDOWN
            case ToolbarMenuId.PROPERTIES:
                this.showRundownProperties();
                break;
            case ToolbarMenuId.RUNDOWN_RENAME:
                this.renameRundown();
                break;
            case ToolbarMenuId.RUNDOWN_EXPORT_TXT:
                this.exportGridToTxt();
                break;
            case ToolbarMenuId.ARCHIVE_ALL_ROWS:
                this.archiveRundown(false, false);
                break;
            case ToolbarMenuId.ARCHIVE_ABOVE_BLACKLINE:
                this.archiveRundown(false, true);
                break;
            case ToolbarMenuId.ARCHIVE_COPY_ALL:
                this.archiveRundown(true, false);
                break;
            case ToolbarMenuId.ARCHIVE_COPY_ABOVE_BLACKLINE:
                this.archiveRundown(true, true);
                break;
            // View
            case ToolbarMenuId.DELETE:
                if (this.data()?.Path?.isTemplate()) {
                    this.store.dispatch(RecordHttpActions.deleteRecordTemplate({ guid: this.data().Guid, recordType: this.data().Type }));
                    break;
                }
                this.store.dispatch(RecordHttpActions.deleteRecord({ record: this.data() }));
                break;
            case ToolbarMenuId.LAYOUT_MAKE_USER:
                this.makeThisMyUserLayout();
                break;
            case ToolbarMenuId.LAYOUT_MAKE_GROUP:
                this.makeThisMyGroupLayout();
                break;
            case ToolbarMenuId.LAYOUT_SAVE_NEW:
                this.saveAsNewLayout();
                break;
            case ToolbarMenuId.LAYOUT_SELECTED:
                // TODO: since we have the fullscreen area input, we can remove the onLayoutSave output or even both outputs
                this.columnsChanged.set(false);
                this.layoutChange.emit({ name: (item.metadata as { name: string })?.name, type: LayoutType.Stored });
                break;
            case ToolbarMenuId.LAYOUT_DEFAULT:
                // TODO: since we have the fullscreen area input, we can remove the onLayoutSave output or even both outputs
                this.columnsChanged.set(false);
                this.layoutChange.emit({ name: SystemLayoutNames.DEFAULT, type: LayoutType.DefaultLayout });
                break;
            case ToolbarMenuId.LAYOUT_USER:
                // TODO: since we have the fullscreen area input, we can remove the onLayoutSave output or even both outputs
                this.columnsChanged.set(false);
                this.layoutChange.emit({ name: SystemLayoutNames.USER, type: LayoutType.User });
                break;
            case ToolbarMenuId.LAYOUT_GROUP:
                // TODO: since we have the fullscreen area input, we can remove the onLayoutSave output or even both outputs
                this.columnsChanged.set(false);
                this.layoutChange.emit({ name: SystemLayoutNames.GROUP, type: LayoutType.Group });
                break;
            case ToolbarMenuId.LAYOUT_SYSTEM:
                // TODO: since we have the fullscreen area input, we can remove the onLayoutSave output or even both outputs
                this.columnsChanged.set(false);
                this.layoutChange.emit({ name: SystemLayoutNames.SYSTEM, type: LayoutType.System });
                break;
            case ToolbarMenuId.LAYOUT_TEMPLATE:
                // TODO: since we have the fullscreen area input, we can remove the onLayoutSave output or even both outputs
                this.columnsChanged.set(false);
                this.layoutChange.emit({ name: SystemLayoutNames.TEMPLATE, type: LayoutType.Template });
                break;
            case ToolbarMenuId.LAYOUT_CHOOSE_COLUMNS:
                this.openChooseColumnsDialog();
                break;
            case ToolbarMenuId.LAYOUT_DELETE:
                this.deleteCurrentLayout();
                break;
            // Production
            case ToolbarMenuId.PRODUCTION_SKIP_UNTIMED:
                this.skipUntimed();
                break;
            case ToolbarMenuId.PRODUCTION_FREEZE_PAGE_NUMBERS:
                this.freezePageNumbers();
                break;
            case ToolbarMenuId.PRODUCTION_ON_AIR:
                this.onAir();
                break;
            case ToolbarMenuId.PRODUCTION_MOS_CONTROL_ACTIVE:
                this.mosControlActive();
                break;
            case ToolbarMenuId.PRODUCTION_LOCK_RUNDOWN:
                this.lockRundown();
                break;
            case ToolbarMenuId.PRODUCTION_LOCK_ALL_STORIES:
                this.lockAllStories();
                break;
            case ToolbarMenuId.PRODUCTION_RECORD:
                this.liveRecording();
                break;
            case ToolbarMenuId.PRODUCTION_LOCK_TIMING_BAR:
                this.toggleLockingTimingBar();
                break;
            case ToolbarMenuId.PRODUCTION_MOS_READY_TO_AIR:
                this.mosReadyToAir();
                break;
            case ToolbarMenuId.PRODUCTION_MOS_STORY_SEND:
                this.mosStorySend();
                break;
            // Insert
            case ToolbarMenuId.INSERT_ROW_ABOVE:
                this.shouldRestoreFocusInGrid = true;
                this.addRow(NewRowPositionEnum.UP);
                break;
            case ToolbarMenuId.INSERT_ROW_BELOW:
                this.shouldRestoreFocusInGrid = true;
                this.addRow(NewRowPositionEnum.DOWN);
                break;
            case ToolbarMenuId.INSERT_BREAK_ROW:
                this.insertBreak();
                break;
            case ToolbarMenuId.SELECT_TEMPLATE:
                if (item?.metadata && 'Guid' in item.metadata) {
                    this.selectRundownTemplate(item?.metadata?.Guid);
                }
                break;
            case ToolbarMenuId.TEMPLATE_SAVE_AS:
                this.openNewTemplateDialog();
                break;
            case ToolbarMenuId.TEMPLATE_DELETE:
                if (item?.metadata && 'Guid' in item.metadata) {
                    this.store.dispatch(RecordHttpActions.deleteRecordTemplate({ guid: item?.metadata?.Guid, recordType: item?.metadata?.Type }));
                }
                break;
            default:
                console.error('unknown command');
                break;
        }
        this.closeAllMenus();
    }
    private storeFrontTime(currentId: string, currentHitTime: Date) {
        for (const collectionScript of this.data().children) {
            const storedFrontTimeProperty = collectionScript?.StoredFrontTime;
            const updateStory = (story: StorytellingStoryRecord, frontTime) => {
                const updateStoryFields: EnpsField[] = [
                    { key: PSTFieldNames.StoredFrontTime, value: frontTime },
                    { key: PSTFieldNames.Type, value: ObjectTypes.SCRIPT },
                ];
                this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: story, fieldsToUpdate: updateStoryFields }));
            };
            if (collectionScript?.Guid === currentId) {
                updateStory(collectionScript, currentHitTime);
                return;
            } else if (storedFrontTimeProperty === null && collectionScript?.Guid !== currentId) {
                updateStory(collectionScript, collectionScript?.FrontTime);
            } else if (storedFrontTimeProperty !== null) {
                // if (storedFrontTimeProperty !== collectionScript.FrontTime) {
                //     this.store.dispatch(LiveApiActions.updateRundownStory({ guid: collectionScript.Guid, propID: 'StoredFrontTime', value: collectionScript.FrontTime }));
                // }
            }
        }
    }
    //     Private Sub StoreFrontTime(CurrentID As String, CurrentHitTime As Date)
    //     For Each CollectionScript In Me.SourceRundownRecord.CollectionScripts
    //         Dim StoredFrontTimeProperty = CollectionScript.RecordPointer.ObjectProperties.Field(ENPSFieldNames.FIELDNAME_STOREDFRONTTIME)
    //         If CollectionScript.RecordPointer.ListData.Guid = CurrentID Then
    //             UpdateRecordProperty(CollectionScript, "StoredFrontTime", CurrentHitTime)
    //             Exit For
    //         ElseIf StoredFrontTimeProperty Is Nothing AndAlso CollectionScript.RecordPointer.ListData.Guid <> CurrentID Then
    //             UpdateRecordProperty(CollectionScript, "StoredFrontTime", CollectionScript.FrontTime)
    //         Else
    //             If Not StoredFrontTimeProperty.FieldValue Is Nothing Then
    //                 Dim StoredFrontTime As Date = DateTimeFunctions.AdjustToLocal(DateTimeFunctions.GetDateFromENPSDateString(CStr(StoredFrontTimeProperty.FieldValue)))
    //                 If Not StoredFrontTime = CollectionScript.FrontTime Then
    //                     UpdateRecordProperty(CollectionScript, "StoredFrontTime", CollectionScript.FrontTime)
    //                 End If
    //             End If
    //         End If
    //     Next
    // End Sub
}
